/* Calzado Audor · ficha de producto — precio y talla
   Ninguna pieza inventa una cifra: las cantidades llegan YA FORMATEADAS desde Liquid
   en `data-*`. Aqui no se multiplica, no se divide y no se formatea moneda, asi que
   un cambio de formato de pesos en el tema no rompe nada.
     · <aud-precio>       escucha el escalon elegido y repinta precio y ahorro.
     · <aud-talla-medida> «Talla: 40 · mide 25 a 25,9 cm» y la hoja de tallas.
   El selector de pares vive en `aud-escalones.js`, que manda el evento `aud:escalon`.
   Se carga como modulo: el navegador lo evalua UNA vez aunque varios bloques lo pidan. */

const EVENTO = 'aud:escalon';

const seccionDe = (elemento) => elemento.closest('.shopify-section') || document;

/* Rellena una plantilla. Sustituye, no calcula: los importes ya vienen en pesos. */
function componer(plantilla, piezas) {
  if (!plantilla) return '';
  let texto = plantilla;
  for (const [clave, valor] of Object.entries(piezas)) {
    texto = texto.split('{' + clave + '}').join(valor);
  }
  return texto;
}

/* Un enlace a #algo dentro de una pestaña cerrada la abre y baja hasta ella. */
function abrirPorAncla() {
  const id = decodeURIComponent((window.location.hash || '').slice(1));
  if (!id) return;

  const destino = document.getElementById(id);
  if (!destino) return;

  const caja = destino.closest('details') || destino.querySelector('details');
  if (caja) caja.open = true;

  destino.scrollIntoView({ block: 'start', behavior: 'smooth' });
}

window.addEventListener('hashchange', abrirPorAncla);
if (window.location.hash) requestAnimationFrame(abrirPorAncla);

/* === <aud-precio> — precio grande, coletilla y ahorro en verde === */
class AudPrecio extends HTMLElement {
  connectedCallback() {
    this.ambito = this.dataset.ambito || '';
    this.refMonto = this.querySelector('[data-aud-monto]');
    this.refAhorro = this.querySelector('[data-aud-ahorro]');

    document.removeEventListener(EVENTO, this.alElegirEscalon);
    document.addEventListener(EVENTO, this.alElegirEscalon);
  }

  disconnectedCallback() {
    document.removeEventListener(EVENTO, this.alElegirEscalon);
  }

  alElegirEscalon = (evento) => {
    const datos = evento.detail;
    if (!datos || datos.ambito !== this.ambito) return;

    if (this.refMonto && datos.porPar) this.refMonto.textContent = datos.porPar;

    if (this.refAhorro) {
      const hay = Boolean(datos.lineaAhorro);
      this.refAhorro.textContent = hay ? datos.lineaAhorro : '';
      this.refAhorro.hidden = !hay;
      /* El anzuelo (lo que ahorraria llevando dos) se ve distinto del ahorro que
         el comprador YA eligio: uno es una invitacion y el otro es un hecho. */
      this.refAhorro.classList.toggle('is-anzuelo', hay && Boolean(datos.esAnzuelo));
    }
  };
}

/* === <aud-talla-medida> — «Talla: 40», su centimetro y la hoja de tallas ===
   Va ANTES del selector de tallas del tema y se queda quieta: no mueve un solo nodo.
   La version anterior metia la fila dentro del <fieldset> de Horizon, y Horizon la
   barria al repintar la seccion —la fila aparecia y desaparecia en un segundo—.
   Ahora solo se pone una clase en la seccion para silenciar el <legend> del tema,
   que dice «Talla» a secas y aqui sobra. Una clase en la seccion sobrevive al
   repintado; un nodo movido, no. */
class AudTallaMedida extends HTMLElement {
  connectedCallback() {
    this.opcion = this.dataset.opcion || 'Talla';
    this.refValor = this.querySelector('[data-aud-talla-valor]');
    this.refMedida = this.querySelector('[data-aud-talla-medida]');
    this.hoja = this.querySelector('[data-aud-hoja]');
    this.plantilla = this.dataset.plantilla || '';
    this.sinDato = this.dataset.sinDato || '';

    try {
      this.medidas = JSON.parse(this.dataset.medidas || '{}');
    } catch {
      this.medidas = {};
    }

    seccionDe(this).classList?.add('aud-con-talla');

    /* El enlace se pinta siempre desde Liquid, porque su destino normal —el medidor
       compartido— vive en OTRA seccion y Liquid no puede verla. Si no existe ni ese
       medidor ni el dialogo propio de este bloque, el enlace no lleva a ninguna parte:
       se esconde aqui. Se falla hacia enseñarlo y se corrige, no al contrario: al
       contrario la guia se encendia en 1 de 56 fichas (P-1). */
    this.enlace = this.querySelector('[data-aud-abrir-guia]');
    if (this.enlace && !this.hoja && !document.getElementById('aud-pop')) {
      this.enlace.hidden = true;
    }

    document.removeEventListener('change', this.alCambiarTalla);
    document.addEventListener('change', this.alCambiarTalla);

    this.addEventListener('click', this.alHacerClic);
    /* En cuanto el dedo o el raton se acerca al enlace, la hoja empieza a bajar. */
    this.addEventListener('pointerenter', this.adelantarLaHoja, true);
    this.addEventListener('focusin', this.adelantarLaHoja);
    this.addEventListener('touchstart', this.adelantarLaHoja, { passive: true });

    this.pintarDesdeLaSeleccion();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.alCambiarTalla);
    this.removeEventListener('click', this.alHacerClic);
    this.removeEventListener('pointerenter', this.adelantarLaHoja, true);
    this.removeEventListener('focusin', this.adelantarLaHoja);
    this.removeEventListener('touchstart', this.adelantarLaHoja);
  }

  /* Una imagen `loading="lazy"` dentro de un <dialog> cerrado NO SE DESCARGA NUNCA:
     el dialogo esta en display:none, el observador de visibilidad no dispara, y al
     abrirlo el navegador no reevalua. Medido el 2026-09-23: cinco segundos con la
     hoja abierta y la imagen seguia sin llegar. Pasarla a `eager` la trae en ~1 s.
     Se hace al acercarse al enlace y, como red, justo antes de abrir. */
  adelantarLaHoja = () => {
    const imagen = this.hoja?.querySelector('img');
    if (imagen && imagen.loading === 'lazy') imagen.loading = 'eager';
  };

  alHacerClic = (evento) => {
    if (!this.hoja) return;
    if (evento.target.closest('[data-aud-abrir-guia]')) {
      /* Si la seccion «Hoja de tallas» esta en la plantilla, el enlace es suyo: abre el
         MEDIDOR, que ademas deja agregar al carrito. Este dialogo se queda de respaldo
         para las plantillas que no la lleven. Se comprueba por el id del medidor y no
         por una bandera de JavaScript: el orden de carga de dos modulos no se controla,
         pero el DOM ya esta cuando alguien hace clic. */
      if (document.getElementById('aud-pop')) return;
      evento.preventDefault();
      this.adelantarLaHoja();
      this.hoja.showModal();
    } else if (evento.target.closest('[data-aud-cerrar]') || evento.target === this.hoja) {
      /* `evento.target === this.hoja` es el clic en el fondo oscuro. */
      this.hoja.close();
    }
  };

  /* Igual que en Liquid: unas fichas llaman a la opcion «Talla» y otras «Talla del calzado».
     Con igualdad estricta la fila se quedaba en la primera talla en las segundas. */
  esLaOpcion(nombre) {
    const a = (nombre || '').toLowerCase();
    const b = this.opcion.toLowerCase();
    return a !== '' && (a.includes(b) || b.includes(a));
  }

  alCambiarTalla = (evento) => {
    const campo = evento.target;
    if (!(campo instanceof HTMLInputElement) || campo.type !== 'radio') return;
    if (!this.esLaOpcion(campo.dataset.optionName)) return;
    if (!seccionDe(this).contains(campo)) return;
    this.pintar(campo.value);
  };

  pintarDesdeLaSeleccion() {
    const elegida = [...seccionDe(this).querySelectorAll('input[type="radio"][data-option-name]')].find(
      (r) => r.checked && this.esLaOpcion(r.dataset.optionName)
    );
    if (elegida) this.pintar(elegida.value);
  }

  pintar(talla) {
    if (!talla) return;
    if (this.refValor) this.refValor.textContent = talla;
    if (!this.refMedida) return;

    const cm = this.medidas[talla];
    this.refMedida.textContent = cm ? componer(this.plantilla, { cm: cm }) : this.sinDato;
    this.refMedida.hidden = !cm && !this.sinDato;
    this.refMedida.classList.toggle('is-pendiente', !cm);

    this.marcarFila(talla);
  }

  /* La fila de la tabla del dialogo. Liquid la marca con la talla del render inicial y ahi se
     queda: el dialogo no se repinta al cambiar de talla. Sin esto, quien entra en la 36 y elige
     la 40 abre la guia y ve marcada otra fila, que es peor que ninguna. */
  marcarFila(talla) {
    const filas = this.querySelectorAll('.aud-talla__tabla tbody tr');
    if (!filas.length) return;
    filas.forEach((fila) => {
      const celda = fila.firstElementChild;
      const suya = celda && celda.textContent.trim() === String(talla);
      fila.classList.toggle('is-suya', suya);
    });
  }
}

if (!customElements.get('aud-precio')) customElements.define('aud-precio', AudPrecio);
if (!customElements.get('aud-talla-medida')) customElements.define('aud-talla-medida', AudTallaMedida);
